import type { CSSProperties } from '@mantine/core';
import {
	Center,
	Divider,
	Group,
	Table as MantineTable,
	Pagination,
	Progress,
	rem,
	Select,
	Text,
	Title,
} from '@mantine/core';
import { useOs } from '@mantine/hooks';
import type { Row, RowData } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import sum from 'lodash-es/sum';
import { memo, useMemo } from 'react';

import { SortAscIcon, SortDescIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';

import type { DataTableFeatures, DataTableInstance } from '../features';
import { useColumnDnD } from '../hooks/use-column-dnd';
import type { TableVariant } from '../types';

const PAGINATION_OPTIONS = [
	{ value: '25', label: '25' },
	{ value: '50', label: '50' },
	{ value: '100', label: '100' },
] as const;

export function DataTable<T extends RowData>({
	table,
	loading = false,
	hidePageSize = false,
	hidePagination = false,
	hideItemCount = true,
	itemCountLabel = undefined,
	enableColumnReordering = false,
	variant = 'apple-table',
	columnOrder = [],
	onColumnOrderChange = undefined,
	minWidth,
	renderEmpty,
}: {
	table: DataTableInstance<T>;
	loading: boolean;
	hidePageSize?: boolean;
	hidePagination?: boolean;
	hideItemCount?: boolean;
	itemCountLabel?: string;
	enableColumnReordering?: boolean;
	minWidth?: CSSProperties['minWidth'];
	variant?: TableVariant;
	columnOrder?: string[];
	onColumnOrderChange?: React.Dispatch<React.SetStateAction<string[]>>;
	renderEmpty?: () => React.ReactNode;
}) {
	const { t } = useTranslation('controls');
	const noRows = table.getRowModel().rows.length === 0;

	return (
		<>
			{!loading && noRows ? (
				<Center h='100%'>
					{renderEmpty ? (
						renderEmpty()
					) : (
						<Title mt='xl' order={3}>
							{t('dataTable.empty')}
						</Title>
					)}
				</Center>
			) : (
				<>
					<MantineTable.ScrollContainer
						minWidth={minWidth ?? table.getTotalSize()}
						maw='100%'
						h='100%'
					>
						<TableBody
							columnOrder={columnOrder}
							onColumnOrderChange={onColumnOrderChange}
							table={table}
							variant={variant}
							loading={loading}
							enableColumnReordering={enableColumnReordering}
						/>
					</MantineTable.ScrollContainer>
					<TablePagingButtons
						table={table}
						hidePageSize={hidePageSize}
						hidePagination={hidePagination}
						pageSizeLabel={t('dataTable.pageSize')}
						hideItemCount={hideItemCount}
						itemCountLabel={itemCountLabel ?? t('dataTable.itemCountLabel')}
					/>
				</>
			)}
		</>
	);
}

export function TableBody<TRow extends RowData>({
	table,
	striped = true,
	variant = undefined,
	loading = false,
	enableColumnReordering = false,
	columnOrder = [],
	onColumnOrderChange = undefined,
}: {
	table: DataTableInstance<TRow>;
	striped?: boolean;
	variant?: TableVariant;
	loading?: boolean;
	enableColumnReordering?: boolean;
	columnOrder?: string[];
	onColumnOrderChange?: React.Dispatch<React.SetStateAction<string[]>>;
}) {
	const headerGroups = table.getHeaderGroups();
	const headerCount = useMemo(() => sum(headerGroups.map(x => x.headers.length)), [headerGroups]);
	const { rowSelection } = table.state;
	const { DndProvider, SortableWrapper, SortableTh } = useColumnDnD({
		columnOrder,
		setColumnOrder: onColumnOrderChange,
	});

	return (
		<DndProvider>
			<MantineTable
				striped={striped}
				highlightOnHover
				stickyHeader
				variant={variant}
				// w={table.getTotalSize()}
			>
				<MantineTable.Thead bg='var(--apple-color-table-header-bg)'>
					<SortableWrapper>
						{table.getHeaderGroups().map(headerGroup => (
							<MantineTable.Tr key={headerGroup.id}>
								{headerGroup.headers
									.filter(x => !x.column.columnDef.meta?.hideColumn)
									.map(header => {
										const cellContent = (
											<>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column.columnDef.header,
															header.getContext(),
														)}

												<Group
													key={`${header.id}-sort-icon`}
													gap={0}
													pos='absolute'
													top='0'
													right={0}
													h='100%'
													justify='end'
													align='center'
												>
													{{
														asc: <SortAscIcon />,
														desc: <SortDescIcon />,
														false: null,
													}[header.column.getIsSorted() as string] ??
														null}

													{header.column.getIsLastColumn() ? null : (
														<Divider
															key={`${header.id}-divider`}
															size='xs'
															orientation='vertical'
															c='white'
															my={6}
															onMouseDown={header.getResizeHandler()}
															onTouchStart={header.getResizeHandler()}
															onDoubleClick={() =>
																header.column.resetSize()
															}
															style={{
																cursor: 'col-resize',
																userSelect: 'none',
																touchAction: 'none',
																transform:
																	table.options
																		.columnResizeMode ===
																		'onEnd' &&
																	header.column.getIsResizing()
																		? `translateX(${
																				(table.options
																					.columnResizeDirection ===
																				'rtl'
																					? -1
																					: 1) *
																				(table.state
																					.columnResizing
																					.deltaOffset ??
																					0)
																			}px)`
																		: '',
															}}
														/>
													)}
												</Group>
											</>
										);

										const baseProps = {
											colSpan: header.colSpan,
											w: header.getSize(),
											pos: 'relative' as const,
											fz: 'xs' as const,
											fw: 600 as const,
											onClick: header.column.getToggleSortingHandler(),
											style: {
												whiteSpace: 'nowrap',
												cursor: header.column.getCanSort()
													? 'pointer'
													: 'auto',
												userSelect: 'none',
												paddingRight:
													header.column.getIsSorted() ||
													header.column.getCanSort()
														? rem(32)
														: undefined,
											} as React.CSSProperties,
										};

										return enableColumnReordering &&
											!header.column.columnDef.meta?.disableDrag ? (
											<SortableTh
												key={header.id}
												column={header}
												width={header.getSize()}
												{...baseProps}
											>
												{cellContent}
											</SortableTh>
										) : (
											<MantineTable.Th key={header.id} {...baseProps}>
												{cellContent}
											</MantineTable.Th>
										);
									})}
							</MantineTable.Tr>
						))}
						<MantineTable.Tr>
							<MantineTable.Th
								colSpan={headerCount}
								p={0}
								h={loading ? 3 : 0}
								style={{
									transition: 'height 0.5s ease',
								}}
							>
								<Progress
									radius='xl'
									size='xs'
									value={100}
									animated
									striped
									display={loading ? undefined : 'none'}
								/>
							</MantineTable.Th>
						</MantineTable.Tr>
					</SortableWrapper>
				</MantineTable.Thead>

				<MantineTable.Tbody>
					{table.getRowModel().rows.map(row => (
						<TableRow key={row.id} row={row} selected={rowSelection[row.id] ?? false} />
					))}
				</MantineTable.Tbody>
			</MantineTable>
		</DndProvider>
	);
}

const TableRow = memo(function TableRow<TRow extends RowData>({
	row,
	selected,
}: {
	row: Row<DataTableFeatures, TRow>;
	selected: boolean;
}) {
	return (
		<MantineTable.Tr key={row.id} bg={selected ? 'var(--mantine-color-blue-light)' : undefined}>
			{row
				.getVisibleCells()
				.filter(cell => !cell.column.columnDef.meta?.hideColumn)
				.map(cell => (
					<MantineTable.Td key={cell.id} w={cell.column.getSize()}>
						{flexRender(cell.column.columnDef.cell, cell.getContext())}
					</MantineTable.Td>
				))}
		</MantineTable.Tr>
	);
});
TableRow.displayName = 'TableRow';

function TablePagingButtons<TRow extends RowData>({
	table,
	pageSizeLabel,
	hidePageSize,
	hidePagination,
	hideItemCount,
	itemCountLabel,
}: {
	table: DataTableInstance<TRow>;
	pageSizeLabel: string;
	hidePageSize: boolean;
	hidePagination: boolean;
	hideItemCount: boolean;
	itemCountLabel: string;
}) {
	const currentOS = useOs();
	const isMobile = currentOS === 'ios' || currentOS === 'android';

	if (hidePageSize && hidePagination && hideItemCount) return null;

	return (
		<Group justify='end' align='center' p='lg' pb={isMobile ? '50px' : 'lg'}>
			{!hidePageSize && (
				<>
					<Text>{pageSizeLabel}</Text>
					<Select
						data={PAGINATION_OPTIONS}
						w={rem(100)}
						checkIconPosition='right'
						value={table.state.pagination.pageSize.toString()}
						onChange={value => value && table.setPageSize(Number(value))}
					/>
				</>
			)}
			{!hidePagination && (
				<Pagination
					size='sm'
					total={table.getPageCount()}
					value={table.state.pagination.pageIndex + 1}
					onChange={pageNumber => table.setPageIndex(pageNumber - 1)}
				/>
			)}

			{!hideItemCount && <Text>{`(${table.getRowCount()} ${itemCountLabel})`}</Text>}
		</Group>
	);
}
